/**
 * PRO (Patient-Reported Outcome) Calendar — 18 items across 4 clinical domains.
 * Mirrors prototype mock-data.js `proAreas` structure.
 */

export type ProFrequency =
	| "daily"
	| "weekly"
	| "biweekly"
	| "monthly"
	| "quarterly";

export interface ProItem {
	id: string;
	name: string; // Korean item name
	areaIndex: number; // 0–3 → parent area group
	subGroup: string; // sub-group label within area
	frequency: ProFrequency;
	frequencyLabel: string; // Korean frequency label
}

export interface ProArea {
	index: number;
	label: string; // Korean area header
	subGroups: string[];
}

// ── Area definitions ──
export const PRO_AREAS: ProArea[] = [
	{
		index: 0,
		label: "정서·인지 증상 관리",
		subGroups: ["정서 증상", "약물 관련", "수면·피로"],
	},
	{
		index: 1,
		label: "신체·생활습관 요인 관리",
		subGroups: ["건강행태", "일상생활 구조 유지", "관계 회복", "역할 수행"],
	},
	{
		index: 2,
		label: "사회·환경적 지원",
		subGroups: ["의료자원", "복지자원", "지역사회 사례관리", "디지털 자기기록"],
	},
	{
		index: 3,
		label: "자기관리 및 가족·자원 연계",
		subGroups: [
			"질병 인식",
			"자기효능감",
			"보호자 이해도·돌봄 역량",
			"돌봄 부담 및 안전",
		],
	},
];

// ── 18 PRO items ──
export const PRO_ITEMS: ProItem[] = [
	// Area 0: 정서·인지 증상 관리 — 정서 증상
	{
		id: "pro-depress",
		name: "지속적인 우울감",
		areaIndex: 0,
		subGroup: "정서 증상",
		frequency: "weekly",
		frequencyLabel: "주1회",
	},
	{
		id: "pro-suicide",
		name: "자살 관련 행동",
		areaIndex: 0,
		subGroup: "정서 증상",
		frequency: "monthly",
		frequencyLabel: "월1회",
	},
	// Area 0: 정서·인지 증상 관리 — 약물 관련
	{
		id: "pro-med",
		name: "복약 여부",
		areaIndex: 0,
		subGroup: "약물 관련",
		frequency: "daily",
		frequencyLabel: "매일",
	},
	// Area 0: 정서·인지 증상 관리 — 수면·피로
	{
		id: "pro-sleep",
		name: "불면·과수면 에너지 저하",
		areaIndex: 0,
		subGroup: "수면·피로",
		frequency: "biweekly",
		frequencyLabel: "2주1회",
	},

	// Area 1: 신체·생활습관 요인 관리 — 건강행태
	{
		id: "pro-activity",
		name: "신체활동 악화",
		areaIndex: 1,
		subGroup: "건강행태",
		frequency: "monthly",
		frequencyLabel: "월1회",
	},
	{
		id: "pro-alcohol",
		name: "고위험 음주 여부",
		areaIndex: 1,
		subGroup: "건강행태",
		frequency: "monthly",
		frequencyLabel: "월1회",
	},
	// Area 1: 신체·생활습관 요인 관리 — 일상생활 구조 유지
	{
		id: "pro-meal",
		name: "규칙적인 식사 유지",
		areaIndex: 1,
		subGroup: "일상생활 구조 유지",
		frequency: "weekly",
		frequencyLabel: "주1회",
	},
	// Area 1: 신체·생활습관 요인 관리 — 관계 회복
	{
		id: "pro-support",
		name: "정서적 지지망 존재",
		areaIndex: 1,
		subGroup: "관계 회복",
		frequency: "monthly",
		frequencyLabel: "월1회",
	},
	// Area 1: 신체·생활습관 요인 관리 — 역할 수행
	{
		id: "pro-role",
		name: "사회적 역할 수행 어려움",
		areaIndex: 1,
		subGroup: "역할 수행",
		frequency: "monthly",
		frequencyLabel: "월1회",
	},

	// Area 2: 사회·환경적 지원 — 의료자원
	{
		id: "pro-medresource",
		name: "일차의료기관 이용 가능성",
		areaIndex: 2,
		subGroup: "의료자원",
		frequency: "monthly",
		frequencyLabel: "월1회",
	},
	{
		id: "pro-emergency",
		name: "위기 시 비상 연락",
		areaIndex: 2,
		subGroup: "의료자원",
		frequency: "monthly",
		frequencyLabel: "월1회",
	},
	// Area 2: 사회·환경적 지원 — 복지자원
	{
		id: "pro-welfare",
		name: "정신건강복지센터 연계",
		areaIndex: 2,
		subGroup: "복지자원",
		frequency: "monthly",
		frequencyLabel: "월1회",
	},
	// Area 2: 사회·환경적 지원 — 지역사회 사례관리
	{
		id: "pro-community",
		name: "사례관리 네트워크",
		areaIndex: 2,
		subGroup: "지역사회 사례관리",
		frequency: "monthly",
		frequencyLabel: "월1회",
	},
	// Area 2: 사회·환경적 지원 — 디지털 자기기록
	{
		id: "pro-selfrecord",
		name: "수면 자가 기록",
		areaIndex: 2,
		subGroup: "디지털 자기기록",
		frequency: "biweekly",
		frequencyLabel: "2주1회",
	},

	// Area 3: 자기관리 및 가족·자원 연계 — 질병 인식
	{
		id: "pro-illness",
		name: "응급 상황 대처 방법 인지",
		areaIndex: 3,
		subGroup: "질병 인식",
		frequency: "monthly",
		frequencyLabel: "월1회",
	},
	// Area 3: 자기관리 및 가족·자원 연계 — 자기효능감
	{
		id: "pro-efficacy",
		name: "일상관리 지속 의지",
		areaIndex: 3,
		subGroup: "자기효능감",
		frequency: "monthly",
		frequencyLabel: "월1회",
	},
	// Area 3: 자기관리 및 가족·자원 연계 — 보호자 이해도·돌봄 역량
	{
		id: "pro-caregiver",
		name: "보호자의 질병 이해도",
		areaIndex: 3,
		subGroup: "보호자 이해도·돌봄 역량",
		frequency: "quarterly",
		frequencyLabel: "분기별",
	},
	// Area 3: 자기관리 및 가족·자원 연계 — 돌봄 부담 및 안전
	{
		id: "pro-safety",
		name: "주 돌봄자 유무",
		areaIndex: 3,
		subGroup: "돌봄 부담 및 안전",
		frequency: "monthly",
		frequencyLabel: "월1회",
	},
];

// ── Helpers ──

/**
 * Whether a PRO item is scheduled (due) at a given 0-based week index.
 *
 *   daily    → every week
 *   weekly   → every week
 *   biweekly → weeks 0, 2, 4, 6, …
 *   monthly  → weeks 0, 4, 8, 12, …
 *   quarterly→ weeks 0, 12, 24
 */
export function isScheduled(item: ProItem, weekIndex: number): boolean {
	switch (item.frequency) {
		case "daily":
		case "weekly":
			return true;
		case "biweekly":
			return weekIndex % 2 === 0;
		case "monthly":
			return weekIndex % 4 === 0;
		case "quarterly":
			return weekIndex % 12 === 0;
		default:
			return false;
	}
}

/** CSS modifier class for frequency chip colouring. */
export function getFrequencyClass(frequency: ProFrequency): string {
	return `pro-chip--${frequency}`;
}
